/**
 * results_charts.ts
 *
 * Chart.js visualisations for experiment results:
 * - Elo / accuracy bar chart with a CI-capsule + mean-dot interval plot
 * - Cost-vs-score scatter chart with labels and cost-range error bars
 *
 * Chart data is read from <script type="application/json"> elements:
 *   #elo-chart-data           { scores, ci, modelNames }               (pairwise)
 *   #cost-elo-chart-data      { costData, scores, modelNames }         (pairwise)
 *   #accuracy-chart-data      { overall, questions, modelNames }       (survey)
 *   #cost-accuracy-chart-data { costData, scores, modelNames }         (survey)
 */

interface EloScore {
    condition: string;
    score: number;
}

interface CIItem {
    condition: string;
    lower: number;
    upper: number;
}

interface AccuracyScore {
    condition: string;
    value: number;
    lower: number;
    upper: number;
    n: number;
    n_yes: number;
}

interface AccuracyQuestion {
    id: string;
    text: string;
    scope: string;
    prompt_index: number | null;
    prompt: string | null;
    models: AccuracyScore[];
}

interface CostDataItem {
    slug: string;
    model_id: string;
    avg_cost: number;
    min_cost: number;
    max_cost: number;
    count: number;
}

// Pastel rainbow palette — matches the designer mockup. Cycles per rank.
const RANK_PALETTE = [
    '#56B0DA',
    '#6283D0',
    '#AC5FE5',
    '#E55F9D',
    '#DEAA55',
    '#8CCA57',
    '#5FB1A2',
    '#C77B5A',
];

function modelName(id: string, names: Record<string, string>): string {
    return names[id] || id.split('/').pop() || id;
}

function isDarkMode(): boolean {
    return document.documentElement.classList.contains('dark');
}

// Narrow (phone) viewports get less horizontal room, so bar-chart axis labels
// clip and scatter labels pile up. Below this width we shorten axis labels and
// collision-drop scatter labels; the full name always stays in the tooltip.
function isNarrowViewport(): boolean {
    return window.innerWidth < 640;
}

function truncateLabel(s: string, max: number): string {
    return s.length > max ? s.slice(0, max - 1).trimEnd() + '…' : s;
}

function applyChartTheme(): void {
    const dark = isDarkMode();
    const textColor = dark ? '#dee2e6' : '#666';
    const gridColor = dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
    Chart.defaults.color = textColor;
    Chart.defaults.borderColor = gridColor;
}

// Destroy any Chart instance bound to one of these canvases — avoids the
// "Canvas is already in use" error on HTMX re-swaps, theme changes, and the
// survey question selector re-rendering onto the same canvas.
function destroyChartsOn(canvases: Array<HTMLCanvasElement | null>): void {
    const existing = Chart.instances;
    for (const key of Object.keys(existing)) {
        const inst = existing[key as any];
        if (canvases.includes(inst.canvas)) {
            inst.destroy();
        }
    }
}

// ────────────────────────────────────────────────────────────────
// Shared interval bar chart (CI capsule + mean dot + value label)
// ────────────────────────────────────────────────────────────────
interface IntervalChartSpec {
    canvas: HTMLCanvasElement;
    labels: string[];
    // Floating bars [lower, upper]; a zero-width capsule for a missing CI.
    capsules: Array<[number, number]>;
    means: number[];
    // Upper bound per row, used to place the value label after the capsule.
    highs: number[];
    colors: string[];
    // Whether each row has a real CI (white dot on the capsule) or not
    // (color-filled dot standing alone).
    hasCI: boolean[];
    xTitle: string;
    xMin: number;
    xMax: number;
    tooltipLabel: (i: number) => string[];
    valueLabel: (i: number) => string;
}

function renderIntervalChart(spec: IntervalChartSpec): void {
    const {
        canvas,
        labels,
        capsules,
        means,
        highs,
        colors,
        hasCI,
        xTitle,
        xMin,
        xMax,
        tooltipLabel,
        valueLabel,
    } = spec;

    // Dynamic height: 35px per bar, minimum 200px
    const chartHeight = Math.max(200, labels.length * 35);
    canvas.parentElement!.style.height = chartHeight + 'px';

    new Chart(canvas, {
        type: 'bar',
        data: {
            labels,
            datasets: [
                {
                    data: capsules,
                    backgroundColor: colors,
                    borderWidth: 0,
                    borderRadius: Number.MAX_VALUE,
                    borderSkipped: false,
                    barThickness: 14,
                },
            ],
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            // Room for the mean value printed after each capsule.
            layout: { padding: { right: 56 } },
            // Hover anywhere on a row — narrow capsules are tiny targets.
            interaction: { mode: 'nearest', axis: 'y', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (tooltipCtx: any) =>
                            tooltipLabel(tooltipCtx.dataIndex),
                    },
                },
            },
            scales: {
                x: {
                    title: { display: true, text: xTitle },
                    min: xMin,
                    max: xMax,
                },
                y: {
                    ticks: {
                        autoSkip: false,
                        callback: (_v: any, index: number) =>
                            isNarrowViewport()
                                ? truncateLabel(labels[index] ?? '', 18)
                                : labels[index],
                    },
                },
            },
        },
        plugins: [
            {
                id: 'meanMarkers',
                afterDatasetsDraw: (chart: any) => {
                    const ctx2 = chart.ctx;
                    const meta = chart.getDatasetMeta(0);
                    const area = chart.chartArea;
                    const xScale = chart.scales.x;
                    const labelColor = isDarkMode() ? '#adb5bd' : '#555';

                    meta.data.forEach((bar: any, i: number) => {
                        const y = bar.y;

                        // Mean dot — white on the colored capsule (filled with
                        // the rank color when there's no capsule to sit on).
                        const xMean = xScale.getPixelForValue(means[i]);
                        if (xMean >= area.left && xMean <= area.right) {
                            ctx2.save();
                            ctx2.beginPath();
                            ctx2.arc(xMean, y, 4, 0, Math.PI * 2);
                            ctx2.shadowColor = 'rgba(0, 0, 0, 0.25)';
                            ctx2.shadowBlur = 3;
                            ctx2.fillStyle = hasCI[i] ? '#fff' : colors[i];
                            ctx2.fill();
                            ctx2.shadowColor = 'transparent';
                            ctx2.lineWidth = 1.5;
                            ctx2.strokeStyle = 'rgba(0, 0, 0, 0.28)';
                            ctx2.stroke();
                            ctx2.restore();
                        }

                        // Mean value after the capsule, where the table's
                        // numbers used to live.
                        const xEnd = Math.min(
                            xScale.getPixelForValue(highs[i]),
                            area.right,
                        );
                        ctx2.save();
                        ctx2.font = '11px system-ui, sans-serif';
                        ctx2.fillStyle = labelColor;
                        ctx2.textBaseline = 'middle';
                        ctx2.fillText(valueLabel(i), xEnd + 8, y);
                        ctx2.restore();
                    });
                },
            },
        ],
    });
}

// ────────────────────────────────────────────────────────────────
// Elo bar chart
// ────────────────────────────────────────────────────────────────
function initEloChart(): void {
    const dataEl = document.getElementById('elo-chart-data');
    if (!dataEl) return;

    const parsed = JSON.parse(dataEl.textContent || '{}');
    const scores: EloScore[] = parsed.scores || [];
    const ci: CIItem[] = parsed.ci || [];
    const MODEL_NAMES: Record<string, string> = parsed.modelNames || {};
    if (!scores.length) return;

    // Sort by score descending
    scores.sort((a, b) => b.score - a.score);

    const labels = scores.map((s) => modelName(s.condition, MODEL_NAMES));
    const data = scores.map((s) => s.score);

    // CI lookup
    const ciMap: Record<string, CIItem> = {};
    ci.forEach((c) => {
        ciMap[c.condition] = c;
    });

    const colors = scores.map((_, i) => RANK_PALETTE[i % RANK_PALETTE.length]);

    const ctx = document.getElementById(
        'elo-chart',
    ) as HTMLCanvasElement | null;
    if (!ctx) return;

    // Interval plot: each model gets a rounded capsule spanning its 95%
    // credible interval (a Chart.js floating bar [lower, upper] with full
    // border radius), with a dot marking the mean. Models without a CI get
    // a zero-width capsule and a color-filled dot instead.
    const capsules: Array<[number, number]> = scores.map((s) => {
        const c = ciMap[s.condition];
        return c ? [c.lower, c.upper] : [s.score, s.score];
    });
    const hasCI = scores.map((s) => Boolean(ciMap[s.condition]));

    // Fit the x-axis to the data. Elo scores cluster around ~1000, so
    // Chart.js's default zero-based value axis crushes all the variation
    // into a sliver at the right edge. Fit to the scores, then let CIs widen
    // the axis only up to half a score-range per side — a model with barely
    // any signal can have a CI spanning thousands (lost every comparison ⇒
    // unbounded lower tail), and fitting to that would crush everything else
    // just as badly. A capsule cut off by the axis clips to a flat edge,
    // which reads as "continues beyond the chart"; the tooltip always shows
    // the true bounds.
    const scoreMin = Math.min(...data);
    const scoreMax = Math.max(...data);
    const scoreRange = scoreMax - scoreMin || 100;
    const lows = capsules.map((c) => c[0]);
    const highs = capsules.map((c) => c[1]);
    const xMin = Math.max(Math.min(...lows), scoreMin - scoreRange * 0.5);
    const xMax = Math.min(Math.max(...highs), scoreMax + scoreRange * 0.5);
    const xPad = (xMax - xMin) * 0.05;

    renderIntervalChart({
        canvas: ctx,
        labels,
        capsules,
        means: data,
        highs,
        colors,
        hasCI,
        xTitle: 'Elo score',
        xMin: Math.floor(xMin - xPad),
        xMax: Math.ceil(xMax + xPad),
        tooltipLabel: (i) => {
            const s = scores[i];
            const c = ciMap[s.condition];
            const lines = ['Elo (mean): ' + s.score.toFixed(1)];
            if (c) {
                lines.push(
                    '95% CI: ' +
                        c.lower.toFixed(1) +
                        ' – ' +
                        c.upper.toFixed(1),
                );
            }
            return lines;
        },
        valueLabel: (i) => String(Math.round(data[i])),
    });
}

// ────────────────────────────────────────────────────────────────
// Plain horizontal bar chart (survey accuracy) — no interval capsule
// ────────────────────────────────────────────────────────────────
interface PlainBarChartSpec {
    canvas: HTMLCanvasElement;
    labels: string[];
    values: number[];
    colors: string[];
    xTitle: string;
    xMin: number;
    xMax: number;
    tooltipLabel: (i: number) => string[];
    valueLabel: (i: number) => string;
}

function renderPlainBarChart(spec: PlainBarChartSpec): void {
    const {
        canvas,
        labels,
        values,
        colors,
        xTitle,
        xMin,
        xMax,
        tooltipLabel,
        valueLabel,
    } = spec;

    // Dynamic height: 35px per bar, minimum 200px (matches the interval chart)
    const chartHeight = Math.max(200, labels.length * 35);
    canvas.parentElement!.style.height = chartHeight + 'px';

    new Chart(canvas, {
        type: 'bar',
        data: {
            labels,
            datasets: [
                {
                    data: values,
                    backgroundColor: colors,
                    borderWidth: 0,
                    borderRadius: Number.MAX_VALUE,
                    borderSkipped: false,
                    barThickness: 14,
                },
            ],
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            // Room for the value printed after each bar.
            layout: { padding: { right: 56 } },
            interaction: { mode: 'nearest', axis: 'y', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (tooltipCtx: any) =>
                            tooltipLabel(tooltipCtx.dataIndex),
                    },
                },
            },
            scales: {
                x: {
                    title: { display: true, text: xTitle },
                    min: xMin,
                    max: xMax,
                },
                y: {
                    ticks: {
                        autoSkip: false,
                        callback: (_v: any, index: number) =>
                            isNarrowViewport()
                                ? truncateLabel(labels[index] ?? '', 18)
                                : labels[index],
                    },
                },
            },
        },
        plugins: [
            {
                id: 'valueLabels',
                afterDatasetsDraw: (chart: any) => {
                    const ctx2 = chart.ctx;
                    const meta = chart.getDatasetMeta(0);
                    const area = chart.chartArea;
                    const xScale = chart.scales.x;
                    const labelColor = isDarkMode() ? '#adb5bd' : '#555';

                    meta.data.forEach((bar: any, i: number) => {
                        const xEnd = Math.min(
                            xScale.getPixelForValue(values[i]),
                            area.right,
                        );
                        ctx2.save();
                        ctx2.font = '11px system-ui, sans-serif';
                        ctx2.fillStyle = labelColor;
                        ctx2.textBaseline = 'middle';
                        ctx2.fillText(valueLabel(i), xEnd + 8, bar.y);
                        ctx2.restore();
                    });
                },
            },
        ],
    });
}

// ────────────────────────────────────────────────────────────────
// Accuracy bar chart (survey) — % true with a statement selector
// ────────────────────────────────────────────────────────────────
function initAccuracyChart(): void {
    const dataEl = document.getElementById('accuracy-chart-data');
    if (!dataEl) return;

    const parsed = JSON.parse(dataEl.textContent || '{}');
    const overall: AccuracyScore[] = parsed.overall || [];
    const questions: AccuracyQuestion[] = parsed.questions || [];
    const MODEL_NAMES: Record<string, string> = parsed.modelNames || {};
    if (!overall.length) return;

    const canvas = document.getElementById(
        'accuracy-chart',
    ) as HTMLCanvasElement | null;
    if (!canvas) return;

    // Colors are keyed to each model's OVERALL rank (overall is sorted best-first
    // server-side), so a model keeps its color across selector switches.
    const colorByCondition: Record<string, string> = {};
    overall.forEach((s, i) => {
        colorByCondition[s.condition] = RANK_PALETTE[i % RANK_PALETTE.length];
    });

    function renderView(models: AccuracyScore[]): void {
        destroyChartsOn([canvas]);
        if (!models.length) return;
        renderPlainBarChart({
            canvas: canvas!,
            labels: models.map((m) => modelName(m.condition, MODEL_NAMES)),
            values: models.map((m) => m.value),
            colors: models.map(
                (m) => colorByCondition[m.condition] || RANK_PALETTE[0],
            ),
            xTitle: 'Accuracy (% true)',
            xMin: 0,
            xMax: 100,
            tooltipLabel: (i) => {
                const m = models[i];
                return [
                    'True: ' +
                        m.value.toFixed(1) +
                        '% (' +
                        m.n_yes +
                        '/' +
                        m.n +
                        ')',
                ];
            },
            valueLabel: (i) => Math.round(models[i].value) + '%',
        });
    }

    const select = document.getElementById(
        'accuracy-question-select',
    ) as HTMLSelectElement | null;

    function render(): void {
        let models = overall;
        if (select && select.value !== 'overall') {
            const q = questions.find((qq) => qq.id === select.value);
            models = q ? q.models : [];
        }
        renderView(models);
    }

    if (select && !select.dataset.accuracyBound) {
        select.dataset.accuracyBound = 'true';
        select.addEventListener('change', render);
    }
    render();
}

// ────────────────────────────────────────────────────────────────
// Cost vs score scatter chart (shared by pairwise Elo and survey accuracy)
// ────────────────────────────────────────────────────────────────
// One model's score row from the embedded results json_script. Elo carries
// `score`, survey accuracy carries `value`; each chart reads its own field
// through the spec's `scoreOf` accessor.
interface ScoreEntry {
    condition: string;
    score?: number;
    value?: number;
}

interface CostScatterSpec {
    dataElId: string;
    canvasId: string;
    // Reads the y value off a score entry (s.score for Elo, s.value for survey
    // accuracy) — a typed accessor instead of stringly-typed field indexing.
    scoreOf: (s: ScoreEntry) => number;
    yTitle: string;
    // Fixed y-axis range (survey: [0, 100]); when omitted the axis fits the data.
    yFixed?: [number, number];
    // Renders the y portion of the tooltip, e.g. 'Elo 1523.4' or '72.0% true'.
    yLabel: (y: number) => string;
}

function renderCostScatter(spec: CostScatterSpec): void {
    const dataEl = document.getElementById(spec.dataElId);
    if (!dataEl) return;

    const parsed = JSON.parse(dataEl.textContent || '{}');
    const costData: CostDataItem[] = parsed.costData || [];
    if (!costData.length) return;

    const scores: ScoreEntry[] = parsed.scores || [];
    const MODEL_NAMES: Record<string, string> = parsed.modelNames || {};

    const canvas = document.getElementById(
        spec.canvasId,
    ) as HTMLCanvasElement | null;
    if (!canvas) return;

    // Build score lookup from experiment results
    const scoreMap: Record<string, number> = {};
    scores.forEach((s) => {
        scoreMap[s.condition] = spec.scoreOf(s);
    });

    canvas.parentElement!.style.height = '500px';

    // Build scatter points (only models with both cost and score data)
    interface ScatterPoint {
        x: number;
        y: number;
        model_id: string;
        label: string;
        min_cost: number;
        max_cost: number;
        count: number;
    }

    const scatterData: ScatterPoint[] = [];
    costData.forEach((d) => {
        const score = scoreMap[d.slug];
        if (score !== undefined) {
            scatterData.push({
                x: d.avg_cost,
                y: score,
                model_id: d.model_id,
                label: modelName(d.model_id, MODEL_NAMES),
                min_cost: d.min_cost,
                max_cost: d.max_cost,
                count: d.count,
            });
        }
    });

    if (!scatterData.length) return;

    // Compute padded axis limits so labels don't clip at edges.
    // Labels render to the right of points, so x needs more right padding.
    const xValues = scatterData.map((d) => d.x);
    const yValues = scatterData.map((d) => d.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...scatterData.map((d) => d.max_cost));
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const xRange = xMax - xMin || xMax * 0.5;
    const yRange = yMax - yMin || 100;
    const xPadLeft = xRange * 0.05;
    const xPadRight = xRange * 0.25;
    const yPad = yRange * 0.1;

    // Sort by score descending to assign rank colors
    const sorted = scatterData.slice().sort((a, b) => b.y - a.y);
    const rankMap: Record<string, number> = {};
    sorted.forEach((d, i) => {
        rankMap[d.model_id] = i;
    });

    const pointColors = scatterData.map((d) => {
        const rank = rankMap[d.model_id];
        return RANK_PALETTE[rank % RANK_PALETTE.length];
    });

    // Error bars for cost range (horizontal)
    const xErrLow = scatterData.map((d) => d.x - d.min_cost);
    const xErrHigh = scatterData.map((d) => d.max_cost - d.x);

    const yScale = spec.yFixed
        ? { min: spec.yFixed[0], max: spec.yFixed[1] }
        : { min: yMin - yPad, max: yMax + yPad };

    new Chart(canvas, {
        type: 'scatter',
        data: {
            datasets: [
                {
                    data: scatterData,
                    backgroundColor: pointColors,
                    borderColor: pointColors,
                    pointRadius: 7,
                    pointHoverRadius: 9,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (tooltipCtx: any) => {
                            const d = tooltipCtx.raw as ScatterPoint;
                            let str =
                                d.label +
                                ': ' +
                                spec.yLabel(d.y) +
                                ', $' +
                                d.x.toFixed(4) +
                                '/img';
                            if (d.count > 1 && d.min_cost !== d.max_cost) {
                                str +=
                                    ' ($' +
                                    d.min_cost.toFixed(4) +
                                    '–$' +
                                    d.max_cost.toFixed(4) +
                                    ')';
                            }
                            return str;
                        },
                    },
                },
            },
            scales: {
                x: {
                    title: { display: true, text: 'Cost per image (USD)' },
                    min: Math.max(0, xMin - xPadLeft),
                    max: xMax + xPadRight,
                    ticks: {
                        callback: (v: number) => '$' + v.toFixed(3),
                    },
                },
                y: {
                    title: { display: true, text: spec.yTitle },
                    ...yScale,
                },
            },
        },
        plugins: [
            {
                id: 'scatterLabels',
                // Draw in afterDatasetsDraw (not afterDraw) so these labels and
                // error bars render beneath the hover tooltip, which Chart.js
                // paints in afterDraw. Otherwise the always-on labels cover it.
                afterDatasetsDraw: (chart: any) => {
                    const ctx2 = chart.ctx;
                    const meta = chart.getDatasetMeta(0);

                    // Draw cost range error bars (horizontal)
                    meta.data.forEach((pt: any, i: number) => {
                        const lo = xErrLow[i];
                        const hi = xErrHigh[i];
                        if (lo < 0.000001 && hi < 0.000001) return;

                        const xScale = chart.scales.x;
                        const val = scatterData[i].x;
                        const xLo = xScale.getPixelForValue(val - lo);
                        const xHi = xScale.getPixelForValue(val + hi);
                        const y = pt.y;

                        ctx2.save();
                        ctx2.strokeStyle = isDarkMode()
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.3)';
                        ctx2.lineWidth = 1;
                        ctx2.beginPath();
                        ctx2.moveTo(xLo, y);
                        ctx2.lineTo(xHi, y);
                        ctx2.moveTo(xLo, y - 3);
                        ctx2.lineTo(xLo, y + 3);
                        ctx2.moveTo(xHi, y - 3);
                        ctx2.lineTo(xHi, y + 3);
                        ctx2.stroke();
                        ctx2.restore();
                    });

                    // Draw model name labels. On phones the points crowd together
                    // and long names run off the right edge, so there we shorten
                    // each name, flip it to the left of its point when it would
                    // overflow, and skip any label that would overlap one already
                    // drawn (the full name is still one tap away in the tooltip).
                    const narrow = isNarrowViewport();
                    const area = chart.chartArea;
                    const drawn: Array<[number, number, number, number]> = [];
                    ctx2.save();
                    ctx2.font = '11px system-ui, sans-serif';
                    ctx2.fillStyle = isDarkMode() ? '#adb5bd' : '#555';
                    ctx2.textBaseline = 'bottom';
                    meta.data.forEach((pt: any, i: number) => {
                        const text = narrow
                            ? truncateLabel(scatterData[i].label, 16)
                            : scatterData[i].label;
                        let x = pt.x + 8;
                        const y = pt.y - 2;
                        if (narrow) {
                            const w = ctx2.measureText(text).width;
                            if (x + w > area.right) x = pt.x - 8 - w;
                            const box: [number, number, number, number] = [
                                x,
                                y - 11,
                                x + w,
                                y,
                            ];
                            const overlaps = drawn.some(
                                (b) =>
                                    box[0] < b[2] &&
                                    box[2] > b[0] &&
                                    box[1] < b[3] &&
                                    box[3] > b[1],
                            );
                            if (overlaps) return;
                            drawn.push(box);
                        }
                        ctx2.fillText(text, x, y);
                    });
                    ctx2.restore();
                },
            },
        ],
    });
}

function initCostEloChart(): void {
    renderCostScatter({
        dataElId: 'cost-elo-chart-data',
        canvasId: 'cost-elo-chart',
        scoreOf: (s) => s.score ?? 0,
        yTitle: 'Elo score',
        yLabel: (y) => 'Elo ' + y.toFixed(1),
    });
}

function initCostAccuracyChart(): void {
    renderCostScatter({
        dataElId: 'cost-accuracy-chart-data',
        canvasId: 'cost-accuracy-chart',
        scoreOf: (s) => s.value ?? 0,
        yTitle: 'Accuracy (% true)',
        yFixed: [0, 100],
        yLabel: (y) => y.toFixed(1) + '% true',
    });
}

// ────────────────────────────────────────────────────────────────
// Boot
// ────────────────────────────────────────────────────────────────
function initCharts(): void {
    destroyChartsOn([
        document.getElementById('elo-chart') as HTMLCanvasElement | null,
        document.getElementById('cost-elo-chart') as HTMLCanvasElement | null,
        document.getElementById('accuracy-chart') as HTMLCanvasElement | null,
        document.getElementById(
            'cost-accuracy-chart',
        ) as HTMLCanvasElement | null,
    ]);
    applyChartTheme();
    initEloChart();
    initCostEloChart();
    initAccuracyChart();
    initCostAccuracyChart();
}

initCharts();

if (!document.documentElement.dataset.chartThemeListenerBound) {
    document.documentElement.dataset.chartThemeListenerBound = 'true';
    window.addEventListener('eval:theme-change', initCharts);
}
